import React, {useContext, useEffect, useRef} from 'react';
import {Animated, RefreshControl, StyleSheet, View} from 'react-native';
import {BottomTabBarHeightContext} from '@react-navigation/bottom-tabs';
import {useBottomInset, useTopInset} from '../../helpers/safeArea';
import {useManagementTheme} from '../../helpers/managementTheme';
import {getFloatingClearance, useManagementLayout} from '../../helpers/managementLayout';
import AnimatedGradientBackground from './AnimatedGradientBackground';
import {TopBar} from './ManagementUI';
import {ScreenKeyboardAvoider} from '../KeyboardAware';
import {useModuleNav} from '../../helpers/useModuleNav';
import ModuleTabBar from '../module/ModuleTabBar';
import NotificationBellButton from '../NotificationBellButton';
import ChatHeaderButton from '../chat/ChatHeaderButton';

/**
 * Standard premium management screen shell:
 *  - slowly drifting gradient background (2026 look)
 *  - optional themed TopBar
 *  - subtle fade + slide-up entrance transition
 *  - optional module-scoped bottom bar (pass `moduleGroup` + `activeModuleTab`)
 *    that replaces the main tab bar while inside a module.
 *
 * The TopBar's right slot defaults to the notification bell (with its unread badge);
 * pass your own `topBar.right` to override it, or `topBar.hideNotifications` to drop it.
 *
 * @param {object} props
 * @param {string} [props.moduleGroup]     Module group id → renders the {@link ModuleTabBar}.
 * @param {string} [props.activeModuleTab] Key of the active module tab (highlight).
 * @param {(view:string)=>void} [props.onModuleView] Handler for `view`-action tabs.
 * @param {Object.<string,number|string>} [props.moduleBadges] Per-tab badges.
 * @param {()=>void} [props.onModuleExit]  Override the exit-to-main-nav behaviour.
 * @param {object} [props.scrollRef]       Optional ref attached to the scroll body (programmatic scrolling).
 * @param {boolean} [props.safeTop=false]  Pad the content below the status bar / notch / Dynamic Island. Set it on
 *   tab-root screens that render no `topBar` and sit under a hidden stack header (greeting headers, hub intros).
 */
export default function ManagementScreen({
    children,
    scroll = true,
    topBar,
    refreshing,
    onRefresh,
    contentContainerStyle,
    intensity,
    floating,
    moduleGroup,
    activeModuleTab,
    onModuleView,
    moduleBadges,
    onModuleExit,
    scrollRef,
    safeTop = false,
}) {
    const m = useManagementTheme();
    const topInset = useTopInset();
    // Optional module-scoped bottom bar. `useModuleNav` returns null when no
    // group is supplied, so this is a no-op on ordinary screens.
    const moduleNav = useModuleNav({
        group: moduleGroup,
        activeTab: activeModuleTab,
        onView: onModuleView,
        onExit: onModuleExit,
        badges: moduleBadges,
    });
    // On tablets / wide screens the content column is centred and width-capped so
    // cards read well instead of stretching edge-to-edge; a no-op on phones.
    const {maxContentWidth} = useManagementLayout();
    const tabBarHeight = useContext(BottomTabBarHeightContext);
    const bottomInset = useBottomInset();
    const floatingClearance = getFloatingClearance({hasModuleBar: !!moduleNav, tabBarHeight, bottomInset});
    const fade = useRef(new Animated.Value(0)).current;
    const slide = useRef(new Animated.Value(18)).current;

    useEffect(() => {
        Animated.parallel([
            Animated.timing(fade, {toValue: 1, duration: 420, useNativeDriver: true}),
            Animated.spring(slide, {toValue: 0, friction: 9, tension: 60, useNativeDriver: true}),
        ]).start();
    }, [fade, slide]);

    const animatedStyle = {opacity: fade, transform: [{translateY: slide}]};

    const column = [styles.column, {maxWidth: maxContentWidth}];

    // Every management screen gets the notification bell in its top bar unless it
    // already fills that slot (or explicitly opts out) — the inbox must be one tap
    // away from ANY module, not only from the handful of screens that hard-coded it.
    // The Team Chat button (unread badge) sits next to the bell for the same reason;
    // it renders nothing when the build or the store/role excludes chat.
    const topBarProps = topBar
        ? {
            ...topBar,
            right: topBar.right ?? (topBar.hideNotifications
                ? null
                : (
                    <View style={styles.topBarActions}>
                        <ChatHeaderButton size={22} color={m.colors.heading} />
                        <NotificationBellButton size={22} color={m.colors.heading} />
                    </View>
                )),
        }
        : null;

    const body = scroll ? (
        <Animated.ScrollView
            ref={scrollRef}
            style={styles.flex}
            contentContainerStyle={[styles.content, safeTop && {paddingTop: 16 + topInset}, contentContainerStyle]}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            refreshControl={onRefresh ? (
                <RefreshControl refreshing={!!refreshing} onRefresh={onRefresh} tintColor={m.colors.primary} />
            ) : undefined}
        >
            <Animated.View style={[animatedStyle, ...column]}>{children}</Animated.View>
        </Animated.ScrollView>
    ) : (
        <Animated.View style={[styles.flex, animatedStyle, ...column, safeTop && {paddingTop: topInset}]}>{children}</Animated.View>
    );

    return (
        <View style={styles.flex}>
            <AnimatedGradientBackground intensity={intensity} />
            {!!topBarProps && <TopBar {...topBarProps} />}
            {/* Keep inputs above the keyboard on every management screen. */}
            <ScreenKeyboardAvoider>{body}</ScreenKeyboardAvoider>
            {/* Optional fixed overlay (e.g. a floating action button) rendered as a
                sibling of the scroll body so it stays pinned while the content scrolls.
                The layer stops above the floating bottom bar so the FAB is never hidden under it. */}
            {!!floating && (
                <View pointerEvents="box-none" style={[styles.floatingLayer, {bottom: floatingClearance}]}>
                    {floating}
                </View>
            )}
            {/* Module-scoped bottom bar (dynamic per-module navigation). */}
            {moduleNav && (
                <ModuleTabBar tabs={moduleNav.tabs} onExit={moduleNav.onExit} menuLabel={moduleNav.menuLabel} />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    flex: {flex: 1},
    topBarActions: {flexDirection: 'row', alignItems: 'center', gap: 20},
    content: {padding: 16, paddingBottom: 120},
    // Centred, width-capped content column for wide screens (no effect on phones,
    // where the real width is always below maxContentWidth).
    column: {width: '100%', alignSelf: 'center'},
    floatingLayer: {position: 'absolute', top: 0, left: 0, right: 0},
});

